import React, { Component } from "react";

/**
 * RubberBand — selection rectangle on mouse drag. Listens on `outerId` (e.g. the
 * grey area) but measures relative to `containerId`, so a marquee started outside
 * the slide still selects boxes.
 * Props: containerId, outerId (defaults to containerId), boxIds, onSelect(ids).
 */
export default class RubberBand extends Component {
  constructor(props) {
    super(props);
    this.state = { active: false, startX: 0, startY: 0, currentX: 0, currentY: 0 };
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this._canvasRect = null;
  }

  listenerId(props = this.props) {
    return props.outerId || props.containerId;
  }

  attachMouseDown(id = this.listenerId()) {
    const el = document.getElementById(id);
    if (el) el.addEventListener("mousedown", this.handleMouseDown);
  }

  detachMouseDown(id = this.listenerId()) {
    const el = document.getElementById(id);
    if (el) el.removeEventListener("mousedown", this.handleMouseDown);
  }

  componentDidMount() {
    this.attachMouseDown();
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("mouseup", this.handleMouseUp);
  }

  componentWillUnmount() {
    this.detachMouseDown();
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseUp);
  }

  componentDidUpdate(prevProps) {
    const prevId = this.listenerId(prevProps);
    const nextId = this.listenerId();
    if (prevId !== nextId) {
      this.detachMouseDown(prevId);
      this.attachMouseDown(nextId);
    }
  }

  handleMouseDown(e) {
    if (e.metaKey || e.ctrlKey || e.button !== 0) return;
    // Ignore presses on boxes or floating controls — they keep their own behaviour.
    if (
      e.target.closest &&
      e.target.closest(
        ".wholebox, #editorBoxIcons, #contained_editorBoxIcons, #multiSelectIcons, button, input, textarea, a, .cke"
      )
    )
      return;

    const container = document.getElementById(this.props.containerId);
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top + container.scrollTop;

    this._canvasRect = rect;
    this.setState({ active: true, startX: x, startY: y, currentX: x, currentY: y });
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.state.active || !this._canvasRect) return;
    const container = document.getElementById(this.props.containerId);
    const scrollTop = container ? container.scrollTop : 0;
    const x = e.clientX - this._canvasRect.left;
    const y = e.clientY - this._canvasRect.top + scrollTop;
    this.setState({ currentX: x, currentY: y });
  }

  handleMouseUp() {
    if (!this.state.active) return;
    this.setState({ active: false });

    const { startX, startY, currentX, currentY } = this.state;
    const selRect = {
      left: Math.min(startX, currentX),
      top: Math.min(startY, currentY),
      right: Math.max(startX, currentX),
      bottom: Math.max(startY, currentY),
    };

    if (selRect.right - selRect.left < 5 && selRect.bottom - selRect.top < 5) return;

    const container = document.getElementById(this.props.containerId);
    if (!container) return;
    const containerRect = container.getBoundingClientRect();

    const selected = this.props.boxIds.filter((id) => {
      const boxEl = document.getElementById("box-" + id);
      if (!boxEl) return false;
      const br = boxEl.getBoundingClientRect();
      const boxLeft = br.left - containerRect.left;
      const boxTop = br.top - containerRect.top;
      const boxRight = boxLeft + br.width;
      const boxBottom = boxTop + br.height;
      return (
        boxLeft < selRect.right &&
        boxRight > selRect.left &&
        boxTop < selRect.bottom &&
        boxBottom > selRect.top
      );
    });

    if (selected.length > 0) this.props.onSelect(selected);
  }

  render() {
    const { active, startX, startY, currentX, currentY } = this.state;
    if (!active) return null;

    const left = Math.min(startX, currentX);
    const top = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    return (
      <div
        style={{
          position: "absolute",
          left,
          top,
          width,
          height,
          border: "1.5px dashed #5b8ef5",
          background: "rgba(91, 142, 245, 0.08)",
          pointerEvents: "none",
          zIndex: 9998,
          boxSizing: "border-box",
        }}
      />
    );
  }
}
